import {apiFiles} from './api-doc-routes.mjs';
import {defaultLanguages, type ZudokuConfig} from 'zudoku';
import Catalog from './src/pages/catalog';
import Home from './src/pages/home';
import AiAssistant from './src/components/AiAssistant';
import LanguageSwitcher from './src/components/LanguageSwitcher';
import LegacyDocsBanner from './src/components/LegacyDocsBanner';
import SiteFooter from './src/components/SiteFooter';
import NotFoundRedirect from './src/components/NotFoundRedirect';
import {WebSocketStreamPlayground} from './src/components/WebSocketStreamsPage';
import generatedNavigation from './src/generated-navigation.json';
import {apiSourceRedirects} from './src/search-routes.mjs';
import customCss from './src/styles.css?raw';

type NavItem = any;
type Locale = 'zh-CN' | 'en';
import {docsBasePath as basePath} from './site-config.mjs';
const localePrefix = (locale: Locale) => locale === 'zh-CN' ? '/zh-CN' : '';
const localizedPath = (locale: Locale, path: string) => `${localePrefix(locale)}/${path.replace(/^\//, '')}`;

const showChinese = () => {
  if (typeof window === 'undefined') return false;
  // Zudoku serializes display callbacks; they must not capture imports or variables.
  return window.location.pathname.split('/').includes('zh-CN');
};

const showEnglish = () => {
  if (typeof window === 'undefined') return true;
  return !window.location.pathname.split('/').includes('zh-CN');
};

const withoutDoc = (items: NavItem[], path: string): NavItem[] => items.flatMap((item) => {
  if (item.type === 'doc') return item.path === path ? [] : [item];
  if (item.type !== 'category') return [item];
  const children = withoutDoc(item.items ?? [], path);
  return children.length ? [{...item, items: children}] : [];
});

function product(locale: Locale, label: string, name: 'spot' | 'futures' | 'copy' | 'wallet' | 'broker' | 'partner', landing: string): NavItem {
  const dir = `${locale}/products/${name}` as keyof typeof generatedNavigation;
  const zh = locale === 'zh-CN';
  const apiBase = {
    spot: 'core-trading-spot',
    futures: 'core-trading-futures',
    copy: 'advanced-trading-copy-trading',
    wallet: 'wallet',
    broker: 'vip-institutional-broker',
    partner: 'vip-institutional-partner',
  }[name];
  const productBase = localizedPath(locale, `catalog/${apiBase}`);
  const restFirstPage = {
    spot: 'configapi',
    futures: 'market-api',
    copy: 'trader',
    wallet: 'assets',
    broker: 'api',
    partner: 'rebate-endpoints',
  }[name];
  const apiLinks: NavItem[] = [
    {type: 'link', label: 'REST API', to: `${productBase}/api/rest-api/${restFirstPage}`},
  ];
  if (name === 'spot' || name === 'futures') {
    apiLinks.unshift({
      type: 'doc',
      label: zh ? 'WebSocket 介绍' : 'WebSocket Introduction',
      file: `${locale}/products/${name}/streams/websocket-intro.md`,
      path: localizedPath(locale, `products/${name}/streams/websocket-intro`),
    });
    apiLinks.push({
      type: 'link',
      label: zh ? 'WebSocket 数据流' : 'WebSocket Streams',
      to: `${productBase}/api/ws-streams/public`,
    });
  }
  return {
    type: 'category', label, collapsible: true, collapsed: true,
    items: name === 'spot' ? [...generatedNavigation[dir], ...apiLinks] : [
      {type: 'doc', label: zh ? '简介' : 'Introduction', file: `${locale}/${landing}`, path: localizedPath(locale, landing.replace(/\.(md|mdx)$/, ''))},
      ...withoutDoc(generatedNavigation[dir], localizedPath(locale, landing.replace(/\.(md|mdx)$/, ''))),
      ...apiLinks,
    ],
  };
}

function docsNavigation(locale: Locale): NavItem {
  const zh = locale === 'zh-CN';
  const introPath = zh ? '/zh-CN/introduction' : '/introduction';
  return {
    type: 'category',
    label: zh ? '文档' : 'Docs',
    icon: 'book-open',
    display: locale === 'zh-CN' ? showChinese : showEnglish,
    link: {type: 'link', to: introPath},
    items: [
      {type: 'filter', placeholder: zh ? '筛选文档' : 'Filter documentation'},
      {type: 'section', label: zh ? '快速开始' : 'Get started'},
      {type: 'doc', file: `${locale}/introduction.md`, path: localizedPath(locale, 'introduction'), label: zh ? '介绍' : 'Introduction'},
      {
        type: 'category', label: zh ? '核心交易' : 'Core Trading', icon: 'chart-no-axes-combined', collapsible: true, collapsed: false,
        items: [
          product(locale, zh ? '现货交易' : 'Spot Trading', 'spot', 'products/spot/introduction/APIBriefIntroduction.md'),
          product(locale, zh ? '合约交易' : 'Futures Trading', 'futures', 'products/futures/intro.md'),
          product(locale, zh ? '钱包' : 'Wallet', 'wallet', 'products/wallet/Introduction.md'),
        ],
      },
      {
        type: 'category', label: zh ? '高级交易' : 'Advanced Trading', icon: 'layers', collapsible: true, collapsed: false,
        items: [
          product(locale, zh ? '跟单交易' : 'Copy Trading', 'copy', 'products/copy/intro.md'),
        ],
      },
      {
        type: 'category', label: zh ? 'VIP 与机构' : 'VIP & Institutional', icon: 'users', collapsible: true, collapsed: false,
        items: [
          product(locale, zh ? '经纪商' : 'Broker', 'broker', 'products/broker/intro.md'),
          product(locale, zh ? '合伙人' : 'Partner', 'partner', 'products/partner/intro.md'),
        ],
      },
    ],
  };
}

function apiDefinition(locale: Locale, input: string, path: string) {
  return {type: 'file' as const, input: `./generated-openapi/${locale}/${input}`, path: localizedPath(locale, `catalog/${path}`)};
}



const config: ZudokuConfig = {
  basePath,
  site: {
    notFoundPage: <NotFoundRedirect />,
    title: 'WEEX Developer Documentation', showPoweredBy: false,
    logo: {src: {light: '/img/weex-logo.svg', dark: '/img/weex-logo.svg'}, alt: 'WEEX', width: 123, href: '/home', reloadDocument: false},
    sidebar: {collapsible: true, toggleVisibility: 'hover', togglePosition: 'center'},
    banner: {message: <LegacyDocsBanner />, color: '#f0b90b', dismissible: true},
    footer: {position: 'start'},
  },
  metadata: {
    title: '%s | WEEX Developer Documentation', defaultTitle: 'WEEX Developer Documentation',
    description: 'Official WEEX V3 API developer documentation', favicon: '/img/favicon.ico',
  },
  header: {placements: {navigation: 'start', search: 'center', auth: 'end'}},
  search: {type: 'pagefind', maxResults: 20, maxSubResults: 4},
  docs: {
    files: '/docs/**/*.{md,mdx}', publishMarkdown: true,
    llms: {
      llmsTxt: false, llmsTxtFull: false, title: 'WEEX API Documentation',
      description: 'Official WEEX V3 API documentation for Spot, Futures, Copy Trading, Wallet, Broker, and Partner integrations.',
    },
    defaultOptions: {toc: true, copyPage: true, disablePager: false, showLastModified: false},
  },
  mdx: {components: {WebSocketStreamPlayground}},
  defaults: {
    apis: {
      examplesLanguage: 'shell',
      supportedLanguages: [
        {value: 'shell', label: 'cURL'},
      ],
      showVersionSelect: 'hide', expandAllTags: true, showInfoPage: false,
      schemaDownload: {enabled: true, fileName: 'weex-openapi-v3'},
    },
  },
  apis: (['zh-CN', 'en'] as const).flatMap((locale) => apiFiles.map(([input, path]) => apiDefinition(locale, input, path))),
  navigation: [
    {type: 'custom-page', path: '/home', label: 'Home', display: () => false, element: <Home locale="en" />},
    {type: 'custom-page', path: '/zh-CN/home', label: '首页', display: () => false, element: <Home locale="zh-CN" />},
    ...generatedNavigation.hiddenEnglishDocuments.map((doc) => ({...doc, display: () => false})),
    {type: 'custom-page', path: '/zh-CN', label: '首页', display: () => false, element: <Home locale="zh-CN" />},
    {type: 'custom-page', path: '/', label: 'Home', display: () => false, element: <Home locale="en" />},
    docsNavigation('zh-CN'),
    docsNavigation('en'),
    {type: 'custom-page', path: '/zh-CN/catalog', label: 'API 参考', icon: 'square-library', display: showChinese, element: <Catalog locale="zh-CN" />},
    {type: 'custom-page', path: '/catalog', label: 'API Reference', icon: 'square-library', display: showEnglish, element: <Catalog locale="en" />},
    {
      type: 'category', label: 'Agent 原生', icon: 'bot', display: showChinese,
      link: {type: 'doc', file: 'zh-CN/agent-native/overview.mdx', path: '/zh-CN/agent-native/overview'},
      items: [
        {type: 'doc', file: 'zh-CN/agent-native/overview.mdx', path: '/zh-CN/agent-native/overview', label: '概览'},
        {type: 'doc', file: 'zh-CN/agent-native/llms-txt.mdx', path: '/zh-CN/agent-native/llms-txt', label: 'llms.txt'},
      ],
    },
    {
      type: 'category', label: 'Agent Native', icon: 'bot', display: showEnglish,
      link: {type: 'doc', file: 'en/agent-native/overview.mdx', path: '/agent-native/overview'},
      items: [
        {type: 'doc', file: 'en/agent-native/overview.mdx', path: '/agent-native/overview', label: 'Overview'},
        {type: 'doc', file: 'en/agent-native/llms-txt.mdx', path: '/agent-native/llms-txt', label: 'llms.txt'},
      ],
    },
    {type: 'link', label: 'WEEX.com', to: 'https://www.weex.com/', target: '_blank', icon: 'external-link'},
  ],
  redirects: [
    ...apiSourceRedirects,
    {from: '/en', to: '/'},
    {from: '/en/introduction', to: '/introduction'},
    {from: '/en/catalog', to: '/catalog'},
    {from: '/en/agent-native/overview', to: '/agent-native/overview'},
    {from: '/en/agent-native/llms-txt', to: '/agent-native/llms-txt'},
    ...(['zh-CN', 'en'] as const).flatMap((locale) => {
      const prefix = localePrefix(locale);
      return [
      {from: `${prefix}/catalog/core-trading-spot/api/rest-api`, to: `${prefix}/catalog/core-trading-spot/api/rest-api/configapi`},
      {from: `${prefix}/catalog/core-trading-spot/api/ws-streams`, to: `${prefix}/catalog/core-trading-spot/api/ws-streams/public`},
      {from: `${prefix}/catalog/core-trading-spot/api/ws-streams/~`, to: `${prefix}/catalog/core-trading-spot/api/ws-streams/public`},
      {from: `${prefix}/catalog/core-trading-spot/api/ws-streams/~endpoints`, to: `${prefix}/catalog/core-trading-spot/api/ws-streams/public`},
      ...Object.entries({general: 'configapi', 'market-data': 'marketdataapi', account: 'accountapi', trading: 'orderapi'}).map(([from, to]) => ({from: `${prefix}/catalog/core-trading-spot/api/rest-api/${from}`, to: `${prefix}/catalog/core-trading-spot/api/rest-api/${to}`})),
      {from: `${prefix}/catalog/core-trading-futures/api/rest-api`, to: `${prefix}/catalog/core-trading-futures/api/rest-api/market-api`},
      {from: `${prefix}/catalog/core-trading-futures/api/ws-streams`, to: `${prefix}/catalog/core-trading-futures/api/ws-streams/public`},
      {from: `${prefix}/catalog/core-trading-futures/api/ws-streams/~`, to: `${prefix}/catalog/core-trading-futures/api/ws-streams/public`},
      {from: `${prefix}/catalog/core-trading-futures/api/ws-streams/~endpoints`, to: `${prefix}/catalog/core-trading-futures/api/ws-streams/public`},
      ...Object.entries({'market-data': 'market-api', account: 'account-api', trading: 'transaction-api'}).map(([from, to]) => ({from: `${prefix}/catalog/core-trading-futures/api/rest-api/${from}`, to: `${prefix}/catalog/core-trading-futures/api/rest-api/${to}`})),
      {from: `${prefix}/catalog/wallet/api/rest-api`, to: `${prefix}/catalog/wallet/api/rest-api/assets`},
      ...Object.entries({CreateSelfTransfer: 'assets', GetDepositAddress: 'wallet', GetDepositHistory: 'wallet', CreateWithdrawal: 'wallet', GetWithdrawalHistory: 'wallet', GetWithdrawalLimit: 'wallet'}).map(([page, group]) => ({from: `${prefix}/products/broker/sub-account/${page}`, to: `${prefix}/products/wallet/${group}/${page}`})),
      {from: `${prefix}/catalog/vip-institutional-broker/api/rest-api`, to: `${prefix}/catalog/vip-institutional-broker/api/rest-api/api`},
      {from: `${prefix}/catalog/advanced-trading-copy-trading/api/rest-api`, to: `${prefix}/catalog/advanced-trading-copy-trading/api/rest-api/trader`},
      {from: `${prefix}/catalog/vip-institutional-broker/api/rest-api/broker`, to: `${prefix}/catalog/vip-institutional-broker/api/rest-api/api`},
      {from: `${prefix}/catalog/vip-institutional-partner/api/rest-api`, to: `${prefix}/catalog/vip-institutional-partner/api/rest-api/rebate-endpoints`},
      {from: `${prefix}/catalog/vip-institutional-partner/api/rest-api/rebates`, to: `${prefix}/catalog/vip-institutional-partner/api/rest-api/rebate-endpoints`},
      {from: `${prefix}/catalog/vip-institutional-partner/api/rest-api/content`, to: `${prefix}/catalog/vip-institutional-partner/api/rest-api/content-endpoints`},
    ];}),
  ],
  theme: {
    customCss, light: {primary: '#b68b00', primaryForeground: '#ffffff'},
    dark: {primary: '#f0c84b', primaryForeground: '#171717', background: '#121416'},
  },
  slots: {
    'head-navigation-end': LanguageSwitcher,
    'mobile-top-bar-end': LanguageSwitcher,
    'footer-before': props => <><SiteFooter {...props} /><AiAssistant /></>,
  },
  syntaxHighlighting: {languages: [...defaultLanguages, 'powershell']},
};

export default config;
